// for backend schemas/types see server/sdk/scheduling-management/schedulingManagementSchemas.ts
import { z } from 'zod';

import { OrderJobFeSchema, OrderSourceEnum } from './orderSchema.ts';
import { OldPagedRequestSchema, PagedResultSchema } from './pagingSchema.ts';
import { WarrantySchema } from './warrantiesSchema.ts';

export const ScheduleBoardCustomerSchema = z.object({
  title: z.string().nullish(),
  initial: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  customerType: z.string().nullish(),
  customerId: z.uuid(),
  email: z.string().nullish(),
  phoneNumber: z.string().nullish(),
});

export const ScheduleBoardSlotItemsSchema = z
  .object({
    id: z.uuid(),
    description: z.string().nullish(),
    parts: z
      .object({
        id: z.uuid(),
        description: z.string().nullish(),
        status: z.string(),
      })
      .array()
      .nullish(),
  })
  .array();

/************************************************** schedule board jobs **************************************************/
export const ScheduleBoardJobSchema = z.object({
  key: z.string().optional(), // added for table rendering purposes
  startDate: z.string(), // consumed by schedule board, includes offset
  endDate: z.string(),
  startDateDisplay: z.string(), // for display purposes, no offset
  endDateDisplay: z.string(),
  text: z.string(),
  state: z.string(),
  laneName: z.string().nullable(),
  laneId: z.string(),
  duration: z.string(),
  jobId: z.string(),
  jobReference: z.string().nullable(),
  jobDescription: z.string().nullable().optional(),
  orderId: z.string(),
  orderReference: z.string().nullable(),
  jobType: z.string(),
  slotType: z.string().nullable().optional(),
  serviceType: z.enum(['Mobile', 'Static']),
  allowResizingReschedule: z.boolean(),
  allowDragAndDropReschedule: z.boolean(),
  fulfilmentTo: z.object({
    reference: z.string().nullish(),
    dataType: z.string().nullish(),
  }),
  customer: ScheduleBoardCustomerSchema,
  scheduleId: z.string().nullish(),
  bookingReference: z.string().nullish(),
  description: z.string().optional(),
  id: z.uuid().optional(),
  defaultDays: z.array(z.string()).optional(),
  source: OrderSourceEnum.optional(),
  items: ScheduleBoardSlotItemsSchema.nullish(),
  technicians: z
    .array(
      z.object({
        userId: z.string(),
        name: z.string(),
      })
    )
    .nullish(),
});

export const ScheduleBoardJobListSchema = z.array(ScheduleBoardJobSchema);

const JobSlotCustomerBESchema = z.object({
  title: z.string().nullish(),
  initial: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  customerType: z.string().nullish(),
  customerId: z.uuid(),
  email: z.string().nullish(),
  phoneNumber: z.string().nullish(),
});

const JobSlotJobScheduledBESchema = z.object({
  id: z.uuid(),
  timeSlotGuid: z.uuid(),
  bookingReference: z.string().nullish(),
  startTime: z.string(),
  date: z.string(),
  endTime: z.string(),
  duration: z.string(),
});

export const JobSlotBESchema = z.object({
  jobId: z.uuid(),
  jobReference: z.string().nullish(),
  jobDescription: z.string().nullish(),
  orderId: z.uuid(),
  orderReference: z.string().nullish(),
  jobType: z.string().nullish(),
  jobStatus: z.string().nullish(),
  slotType: z.string().nullish(),
  serviceType: z.enum(['Mobile', 'Static']),
  allowResizingReschedule: z.boolean(),
  allowDragAndDropReschedule: z.boolean(),
  fulfilmentTo: z.object({
    reference: z.string().nullish(),
    dataType: z.string().nullish(),
  }),
  customer: JobSlotCustomerBESchema,
  scheduled: JobSlotJobScheduledBESchema,
  laneAsset: z.object({
    id: z.uuid().nullish(),
    name: z.string().nullish(),
    type: z.string().nullish(),
  }),
  technicians: z
    .array(
      z.object({
        userId: z.uuid(),
        name: z.string(),
      })
    )
    .nullable(),
  source: z.number(),
  items: ScheduleBoardSlotItemsSchema.nullish(),
});

export type JobSlotBEModel = z.infer<typeof JobSlotBESchema>;

/************************************************** schedule board blocked slots **************************************************/

const BlockedScheduledSchema = z.object({
  id: z.uuid(),
  startTime: z.string(),
  date: z.string(),
  endTime: z.string(),
  duration: z.string(),
});

export const BlockedSlotBESchema = z.object({
  name: z.string().nullish(),
  description: z.string().nullish(),
  allowResizingReschedule: z.boolean(),
  allowDragAndDropReschedule: z.boolean(),
  scheduled: BlockedScheduledSchema,
  laneAsset: z.object({
    id: z.uuid().nullish(),
    name: z.string().nullish(),
    type: z.string().nullish(),
  }),
  status: z.enum(['Authorized', 'Unauthorized']),
  createdBy: z.string(),
  createdDateTime: z.string(),
  modifiedBy: z.string(),
  modifiedDateTime: z.string(),
});
export type BlockedSlotBEModel = z.infer<typeof BlockedSlotBESchema>;

export const ScheduleBoardBlockedSlotSchema = z.object({
  id: z.uuid(),
  startDate: z.string(), // consumed by schedule board, includes offset
  endDate: z.string(),
  duration: z.string().optional(),
  startDateDisplay: z.string(), // for display purposes, no offset
  endDateDisplay: z.string(),
  text: z.string(),
  state: z.string(),
  laneId: z.string(),
  description: z.string().optional(),
  allowResizingReschedule: z.boolean(),
  allowDragAndDropReschedule: z.boolean(),
  serviceLocationId: z.uuid().optional(),
  createdBy: z.string(),
  createdDateTime: z.string(),
  modifiedBy: z.string(),
  modifiedDateTime: z.string(),
  defaultDays: z.array(z.string()).optional(),
});

/************************************************** schedule board jobs and blocked slots **************************************************/

export const ScheduleBoardJobsAndBlockedSlotsSchema = ScheduleBoardJobSchema.or(
  ScheduleBoardBlockedSlotSchema
);

export const ScheduleBoardJobsAndBlockedSlotsListSchema = z.array(
  ScheduleBoardJobsAndBlockedSlotsSchema
);

export const ScheduleWorkshopJobSchema = ScheduleBoardJobSchema.partial({
  customer: true,
  fulfilmentTo: true,
  orderId: true,
  jobId: true,
});

export type ScheduleWorkshopJobModel = z.infer<
  typeof ScheduleWorkshopJobSchema
>;

/************************************************** schedule slots **************************************************/

export const ServiceTypesRequestSchema = z.enum(['Mobile', 'Static']);

export const LocationTypeRequestSchema = z.enum(['Hub', 'Customer', 'Route']);

export const ScheduledSlotsSearchRequestSchema = z.object({
  serviceLocationId: z.string(),
  dateOfService: z.string(),
  serviceTypes: ServiceTypesRequestSchema.optional(),
  locationType: LocationTypeRequestSchema.optional(),
  assetIds: z.array(z.string()).optional(),
  timeZoneOffset: z.string().optional(),
});

export const ScheduleSlotSchema = z.object({
  jobSlots: z.array(JobSlotBESchema).nullish(),
  blockedSlots: z.array(BlockedSlotBESchema).nullish(),
});

export type ScheduleSlotModel = z.infer<typeof ScheduleSlotSchema>;
/************************************************** resources **************************************************/
export const ResourceRequestSchema = z
  .object({
    serviceLocationId: z.string(),
    dateOfService: z.string(),
    serviceType: ServiceTypesRequestSchema.optional(),
  })
  .extend(OldPagedRequestSchema.shape);

export const ResourceAssetSchema = z.object({
  id: z.uuid(),
  type: z.string().nullish(),
  typeId: z.uuid(),
  code: z.string().nullish(),
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  modifiedDateTime: z.string().nullish(),
  defaultDays: z.array(z.string()).nullish(),
});

export const ResourceWithAssetSchema = z.object({
  assignedAssets: z.array(ResourceAssetSchema).nullish(),
  id: z.uuid(),
  name: z.string().nullish(),
  type: z.string().nullish(),
  code: z.string().nullish(),
  modifiedDateTime: z.string().nullish(),
  isActive: z.boolean().nullish(),
  defaultDays: z.array(z.string()).nullish().optional(),
});

export const PagedDataResourceWithAssetSchema = PagedResultSchema.extend({
  results: z.array(ResourceWithAssetSchema).nullish(),
});

/************************************************** order vehicle details **************************************************/

export const OrderVehicleDetailsSearchRequestSchema = z.object({
  orderId: z.uuid(),
});

/************************************************** order job **************************************************/

export const OrderJobSearchRequestSchema = z.object({
  orderId: z.uuid(),
  jobId: z.uuid(),
});

/************************************************** Create Booking **************************************************/

export const JobFulfilmentAddressRequestSchema = z.object({
  serviceLocationId: z.uuid(),
});

export const CreateBookingRequestSchema = z.object({
  orderId: z.string(),
  jobs: OrderJobFeSchema.array(),
  selectedTimeSlot: z.coerce.date<Date>().nullish(),
  assetId: z.uuid(),
  serviceType: z.enum(['Mobile', 'Static']),
  jobFulfilmentAddress: JobFulfilmentAddressRequestSchema,
  locationOffset: z.string().optional(),
});

const CreateBookingJobFulfilmentAddressBESchema = z.object({
  serviceLocationId: z.uuid(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  addressLine3: z.string().nullish(),
  addressLine4: z.string().nullish(),
  postalCode: z.string().nullish(),
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
  timeZone: z.string().nullish(),
  geoLocation: z.object({
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
  }),
});

export const CreateBookingRequestBESchema = z.object({
  jobIds: z.array(z.uuid()).nullish(),
  assetId: z.uuid().nullish(),
  estimatedStartTime: z.string(),
  estimatedFinishTime: z.string(),
  serviceType: z.enum(['Mobile', 'Static']).nullish(),
  jobFulfilmentAddress: CreateBookingJobFulfilmentAddressBESchema,
});

export type CreateBookingRequestBEModel = z.infer<
  typeof CreateBookingRequestBESchema
>;

export const CreateOrderScheduleSchema = z.object({
  orderId: z.uuid(),
  scheduleId: z.uuid(),
  jobIds: z.array(z.uuid()).nullish(),
  bookingReference: z.string().nullish(),
});

export type CreateOrderScheduleModel = z.infer<
  typeof CreateOrderScheduleSchema
>;

/************************************************** Move Job **************************************************/

export const MoveJobRequestSchema = z.object({
  orderId: z.string(),
  scheduleId: z.uuid(),
  bookingReference: z.string(),
  resourceId: z.uuid(),
  resourceCode: z.string(),
  resourceType: z.string(),
  serviceType: z.enum(['Mobile', 'Static']),
  estimatedStartTime: z.string(),
  estimatedFinishTime: z.string(),
  shouldPublishCrmEvent: z.boolean(),
});

/************************************************** Assign Technician **************************************************/

export const AssignTechnicianRequestSchema = z.object({
  assetId: z.uuid(),
  serviceLocationId: z.uuid(),
  technicianId: z.uuid(),
  dateOfService: z.string(),
});

/************************************************** FE **************************************************/
export const TargetedAppointmentDataDateSchema = z.object({
  displayStartDate: z.date(),
  displayEndDate: z.date(),
});

export const JobDataFeSchema = ScheduleBoardJobSchema.or(
  ScheduleBoardBlockedSlotSchema
);

export const TargetedJobDataFeSchema = ScheduleBoardJobSchema.extend(
  TargetedAppointmentDataDateSchema.shape
).or(
  ScheduleBoardBlockedSlotSchema.extend(TargetedAppointmentDataDateSchema.shape)
);

export const JobTicketDataFeSchema = z.object({
  appointmentData: JobDataFeSchema,
  targetedAppointmentData: TargetedJobDataFeSchema,
});

export const JobStatusMapFeSchema = z.array(
  z.object({
    id: z.string(),
    color: z.string(),
  })
);

export const OrderVehicleDetailsFeSchema = z.array(
  z.object({
    key: z.string().nullish(),
    value: z.string().nullish(),
  })
);

export const OrderJobTechnicianFeSchema = z.object({
  userId: z.uuid(),
  name: z.string().nullish(),
  effortPercentage: z.number().int(),
  qcGroupId: z.string().nullish(),
  qcRatingLevel: z.number().int(),
});

export const OrderJobItemFeSchema = z.object({
  itemId: z.uuid(),
  description: z.string().nullish(),
  duration: z.number().int(),
  status: z.string().nullish(),
  warranty: z.array(WarrantySchema).nullish(),
});

export const OrderJobItemListFeSchema = z.array(OrderJobItemFeSchema);
export const CreateBlockedSlotSchema = z.object({
  description: z.string().max(256).optional(),
  assetId: z.uuid(),
  startDateTime: z.string(),
  finishDateTime: z.string(),
});

export const CreateBlockedSlotStaticRequestSchema = z.object({
  serviceLocationId: z.uuid(),
  assetId: z.uuid(),
  startDateTime: z.string(),
  finishDateTime: z.string(),
  description: z.string().nullish(),
});

export type CreateBlockedSlotStaticRequestModel = z.infer<
  typeof CreateBlockedSlotStaticRequestSchema
>;

export const BlockedSlotIdSchema = z.object({
  blockedSlotId: z.uuid(),
});

export type BlockedSlotIdModel = z.infer<typeof BlockedSlotIdSchema>;

export const UpdateBlockedSlotStaticRequestSchema =
  CreateBlockedSlotStaticRequestSchema.extend({
    blockedSlotId: z.uuid(),
  });

export type UpdateBlockedSlotStaticRequestModel = z.infer<
  typeof UpdateBlockedSlotStaticRequestSchema
>;

export const AuthoriseBlockedSlotStaticRequestSchema = z.object({
  blockedSlotId: z.uuid(),
  assetId: z.uuid().optional(),
});

export type AuthoriseBlockedSlotStaticRequestModel = z.infer<
  typeof AuthoriseBlockedSlotStaticRequestSchema
>;

export const EditBlockedSlotSchema = z.object({
  blockedSlotId: z.string(),
  description: z.string().max(256),
  assetId: z.uuid(),
  startDateTime: z.string(),
  finishDateTime: z.string(),
});

export const CreateEditBlockedSlotSchema = CreateBlockedSlotSchema.or(
  EditBlockedSlotSchema
);

export const TriggerOptimizeJobsRequestSchema = z.object({
  dateOfService: z.string(),
  serviceType: ServiceTypesRequestSchema,
  serviceLocationId: z.uuid(),
});

export const OrderScheduleFESchema = z.object({
  id: z.uuid(),
  bookingRef: z.string(),
  duration: z.string(),
  start: z.string().nullish(),
  end: z.string().nullish(),
  jobIds: z.array(z.string()),
});

export const OrderScheduleListFESchema = z.array(OrderScheduleFESchema);

export const SelectedOrderDetailsForAssignTechnicianSchema = z.object({
  orderId: z.string(),
  jobId: z.string(),
});

export const TradingTimesSchema = z
  .object({
    start: z.string(),
    end: z.string(),
  })
  .nullish();

export const JobStatusTagColorSchema = z.object({
  bg: z.string(),
  text: z.string(),
});

export const GetGeoLocationRequestSchema = z.object({
  country: z.string(),
  zip: z.string(),
  address: z.string(),
});
export const GetGeoLocationResponseSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  zip: z.string(),
  isEmpty: z.boolean(),
});

export const AssigTechnicianFormSchema = z.object({
  technician: z.uuid(),
});

export const DurationUnitEnum = z.enum([
  'year',
  'millisecond',
  'second',
  'minute',
  'hour',
  'day',
  'month',
]);

export const DurationRelativePositionEnum = z.enum([
  'onTime',
  'ahead',
  'behind',
]);

/************************************************** schedule slots **************************************************/
export type ScheduledSlotsSearchRequestModel = z.infer<
  typeof ScheduledSlotsSearchRequestSchema
>;

export type ServiceTypesRequestModel = z.infer<
  typeof ServiceTypesRequestSchema
>;
export type LocationTypeRequestModel = z.infer<
  typeof LocationTypeRequestSchema
>;
export type CreateBlockedSlotModel = z.infer<typeof CreateBlockedSlotSchema>;
export type EditBlockedSlotModel = z.infer<typeof EditBlockedSlotSchema>;
export type CreateEditBlockedSlotModel = z.infer<
  typeof CreateEditBlockedSlotSchema
>;
/************************************************** resources **************************************************/
export type ResourceRequestModel = z.infer<typeof ResourceRequestSchema>;
export type ResourceWithAssetModel = z.infer<typeof ResourceWithAssetSchema>;
export type PagedDataResourceWithAssetModel = z.infer<
  typeof PagedDataResourceWithAssetSchema
>;
/************************************************** schedule board jobs **************************************************/
export type ScheduleBoardCustomerModel = z.infer<
  typeof ScheduleBoardCustomerSchema
>;
export type ScheduleBoardJobModel = z.infer<typeof ScheduleBoardJobSchema>;
export type ScheduleBoardJobListModel = z.infer<
  typeof ScheduleBoardJobListSchema
>;

export type ScheduleBoardSlotItemsModel = z.infer<
  typeof ScheduleBoardSlotItemsSchema
>;

/************************************************** schedule board blocked slots **************************************************/
export type ScheduleBoardBlockedSlotModel = z.infer<
  typeof ScheduleBoardBlockedSlotSchema
>;
/************************************************** schedule board jobs and blocked slots **************************************************/
export type ScheduleBoardJobsAndBlockedSlotsModel = z.infer<
  typeof ScheduleBoardJobsAndBlockedSlotsSchema
>;
export type ScheduleBoardJobsAndBlockedSlotsListModel = z.infer<
  typeof ScheduleBoardJobsAndBlockedSlotsListSchema
>;
/************************************************** order vehicle details **************************************************/
export type OrderVehicleDetailsSearchRequestModel = z.infer<
  typeof OrderVehicleDetailsSearchRequestSchema
>;
/************************************************** order job **************************************************/
export type OrderJobSearchRequestModel = z.infer<
  typeof OrderJobSearchRequestSchema
>;
/************************************************** Create Booking **************************************************/
export type CreateBookingRequestModel = z.infer<
  typeof CreateBookingRequestSchema
>;
/************************************************** Move Job **************************************************/
export type MoveJobRequestModel = z.infer<typeof MoveJobRequestSchema>;
/************************************************** Assign Technician **************************************************/
export type AssignTechnicianRequestModel = z.infer<
  typeof AssignTechnicianRequestSchema
>;
/************************************************** FE **************************************************/
export type JobTicketDataFeModel = z.infer<typeof JobTicketDataFeSchema>;
export type JobDataFeModel = z.infer<typeof JobDataFeSchema>;
export type JobStatusMapFeModel = z.infer<typeof JobStatusMapFeSchema>;
export type OrderVehicleDetailsFeModel = z.infer<
  typeof OrderVehicleDetailsFeSchema
>;
export type AssigTechnicianFormModel = z.infer<
  typeof AssigTechnicianFormSchema
>;
export type OrderJobTechnicianFeModel = z.infer<
  typeof OrderJobTechnicianFeSchema
>;
export type OrderJobItemFeModel = z.infer<typeof OrderJobItemFeSchema>;
export type OrderJobItemListFeModel = z.infer<typeof OrderJobItemListFeSchema>;
export type TriggerOptimizeJobsRequestModel = z.infer<
  typeof TriggerOptimizeJobsRequestSchema
>;

export type OrderScheduleFEModel = z.infer<typeof OrderScheduleFESchema>;
export type OrderScheduleListFEModel = z.infer<
  typeof OrderScheduleListFESchema
>;
export type SelectedOrderDetailsForAssignTechnicianModel = z.infer<
  typeof SelectedOrderDetailsForAssignTechnicianSchema
>;
export type TradingTimesModel = z.infer<typeof TradingTimesSchema>;
export type JobStatusTagColorModel = z.infer<typeof JobStatusTagColorSchema>;
export type GetGeoLocationRequestModel = z.infer<
  typeof GetGeoLocationRequestSchema
>;
export type GetGeoLocationResponseModel = z.infer<
  typeof GetGeoLocationResponseSchema
>;
export type DurationUnitType = z.infer<typeof DurationUnitEnum>;
export type DurationRelativePositionType = z.infer<
  typeof DurationRelativePositionEnum
>;
